import EventManager from "../EventManager";

type QueueAction<E> = ((data: E) => Promise<any> | null);

export interface QueueDataAction<E> {
	data: E;
	action: QueueAction<E>;
}

export interface QueueTypeConfiguration<E> {
	action?: QueueAction<E>;
	merge?: ((existing: QueueDataAction<E>, current: QueueDataAction<E>) => QueueDataAction<E>);
	priority: number;
	delay?: number;
	triggers?: string[];
	typeName: string;
}
export interface QueueEntryEventMap<E> {
	"begin":	E;
	"complete":	E;
	"cancel":	E;
}

export class QueueEntry<E> implements QueueDataAction<E> {
	private timeoutId: number | null;
	private ready: boolean;
	private cancelled: boolean = false;

	private readonly eventManager = new EventManager<QueueEntryEventMap<E>>();

	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	constructor (
		private readonly queue: Queue,
		public readonly action: QueueAction<E>,
	    public readonly data: E,
	    public readonly typeConfiguration: QueueTypeConfiguration<E>,
		delay?: number
	) {
		const actualDelay = delay ?? typeConfiguration.delay;
		if (actualDelay === null || actualDelay === undefined || actualDelay < 1) {
			this.ready = true;
			this.timeoutId = null;
		} else {
			this.ready = false;
			this.timeoutId = setTimeout(() => {
				if (!this.isReady()) {
					this.ready = true;
					this.timeoutId = null;
					if (!this.queue.isProcessing()) {
						this.queue.processQueue();
					}
				}
			}, actualDelay);
		}
	}

	public process = () => {
		const identifier = this.queue.startProcessing();
		// console.log(this.typeConfiguration.typeName, identifier);
		if (this.timeoutId !== null) {
			clearTimeout(this.timeoutId);
			this.timeoutId = null;
		}
		let promise;
		try {
			try {
				this.eventManager.notifyEventListeners("begin", this.data);
			} finally {
				promise = this.action(this.data);
			}
		} finally {
			const completed = () => {
				try {
					try {
						this.eventManager.notifyEventListeners("complete", this.data);
					} finally {
						this.eventManager.destroy(); // TODO @Michael: Why is this ran here? It should be called with the Queue is no longer needed.. but...
					}
				} finally {
					try {
						this.queue.processQueue();
					} finally {
						this.queue.doneProcessing(identifier);
					}
				}
			};
			if (promise !== null && promise !== undefined) {
				promise.then(completed, completed);
			} else {
				completed();
			}
		}
	}

	public isReady() {
		return this.ready;
	}
	public isCancelled() {
		return this.cancelled;
	}

	public markReady() {
		if (this.timeoutId !== null) {
			clearTimeout(this.timeoutId);
			this.timeoutId = null;
			this.ready = true;
		}
	}

	static performCancel(entry: QueueEntry<any>) {
		entry.cancelled = true;
		if (entry.timeoutId !== null) {
			clearTimeout(entry.timeoutId);
			entry.timeoutId = null;
		}
		entry.ready = false;
		entry.eventManager.notifyEventListeners("cancel", entry.data);
	}

	public cancel() {
		this.cancelled = true;
		if (this.timeoutId !== null) {
			clearTimeout(this.timeoutId);
			this.timeoutId = null;
		}
		this.ready = false;
		this.eventManager.notifyEventListeners("cancel", this.data);
	}
}

export default class Queue {
	private readonly typeConfigurations = new Map<string, QueueTypeConfiguration<unknown>>();

	private readonly queue = new Array<QueueEntry<unknown>>();

	private readonly processingSet = new Set<number>();

	public registerType<E>(typeConfiguration: QueueTypeConfiguration<E>) {
		this.typeConfigurations.set(typeConfiguration.typeName, typeConfiguration as QueueTypeConfiguration<unknown>);
	}

	public enqueue<E>(type: string, data: E, action?: QueueAction<E>, delay?: number): QueueEntry<E> | null {
		// console.log('pre', type, this.queue, this.processingSet);
		if (!this.typeConfigurations.has(type)) {
			throw `${type} is not a valid type configuration. Valid configurations are in ${[...this.typeConfigurations.keys()].join(', ')}`;
		}
		const queueTypeConfiguration = this.typeConfigurations.get(type) as QueueTypeConfiguration<E>;

		const determinedAction = action ?? queueTypeConfiguration.action;
		if (determinedAction === null || determinedAction === undefined) {
			throw `Attempted to queue an action of a type (${type}) that does not have a default QueueAction without providing a QueueAction function`;
		}

		let result: QueueEntry<E> | null = null;
		if (this.queue.length === 0) {
			this.queue.push((result = new QueueEntry<E>(this, determinedAction, data, queueTypeConfiguration)) as QueueEntry<unknown>);
		} else {
			let i = 0;
			for (; i < this.queue.length; i++) {
				const existing = this.queue[i] as QueueEntry<E>;
				if (existing.isCancelled()) {
					this.queue.splice(i--, 1);
				} else if (existing.typeConfiguration.typeName === type) {
					if (queueTypeConfiguration.merge !== undefined) {
						existing.cancel();
						const {data: mergedData, action: mergedAction} = queueTypeConfiguration.merge(existing, {data, action: determinedAction});

						this.queue[i] = (result = new QueueEntry<E>(this, mergedAction, mergedData, queueTypeConfiguration, delay)) as QueueEntry<unknown>;
						break;
					}
				} else if (existing.typeConfiguration.priority < queueTypeConfiguration.priority) { // This isn't a stack, so we place the lower priority items at the end
					this.queue.splice(i, 0, (result = new QueueEntry<E>(this, determinedAction, data, queueTypeConfiguration, delay)) as QueueEntry<unknown>);
					break;
				}
			}
			if (i >= this.queue.length) {
				this.queue.push((result = new QueueEntry<E>(this, determinedAction, data, queueTypeConfiguration, delay)) as QueueEntry<unknown>);
			}
		}
		if (!this.isProcessing()) {
			// console.log('processing', type, this.queue, this.processingSet);
			this.processQueue();
			return result !== null && this.queue.indexOf(result as QueueEntry<unknown>) > -1 ? result : null;
		}
		// console.log('post', type, this.queue, this.processingSet);
		return result;
	}
	
	public cancel = (type: string | null = null) => {
		if (type === null) {
			this.queue.forEach(QueueEntry.performCancel);
			this.queue.splice(0, this.queue.length);
		}
		for (let i = 0; i < this.queue.length;) {
			let queueElement = this.queue[i]!;
			if (queueElement.typeConfiguration.typeName === type) {
				queueElement.cancel();
				this.queue.splice(i, 1);
			} else {
				i++;
			}
		}
	}

	public isProcessing = () => {
		return this.processingSet.size !== 0;
	}

	public startProcessing = () => {
		const identifier = Math.random();
		this.processingSet.add(identifier);
		return identifier;
	}
	public doneProcessing = (identifier: number) => {
		this.processingSet.delete(identifier);
	}

	public processQueue = () => {
		const identifier = this.startProcessing();
		// console.log('processQueue', identifier, this.processingSet);
		try {
			for (let i = 0; i < this.queue.length; i++) {
				let queueEntry = this.queue[i]!;
				// console.log('checking', queueEntry.typeConfiguration.typeName, this.processingSet);
				if (queueEntry.isReady()) {
					// console.log('processing', queueEntry.typeConfiguration.typeName, this.processingSet);
					this.processQueueEntry(queueEntry);
					break;
				}
			}
		} finally {
			this.doneProcessing(identifier);
		}
	}

	// Note: If the queueEntry triggers something else, we do not necessarily go back to processing the original after processing what it triggered.
	// This is because it is possible for user-interaction to result in the entry being replaced.
	private processQueueEntry = (queueEntry: QueueEntry<unknown>) => {
		const triggers = queueEntry.typeConfiguration.triggers;
		if (triggers !== undefined) {
			for (let i = 0; i < this.queue.length; i++) {
				let item = this.queue[i]!;
				if (!item.isCancelled() && triggers.indexOf(item.typeConfiguration.typeName) > -1) {
					item.markReady();
					this.processQueueEntry(item);
					return;
				}
			}
		}
		this.queue.splice(this.queue.indexOf(queueEntry), 1);
		queueEntry.process();
	}
}
